import React, { useState } from "react";
import { Box, Typography, Button, Alert, Radio, RadioGroup, FormControl, FormLabel, FormControlLabel } from "@mui/material";
import { FiExternalLink } from "react-icons/fi";

const COLORS = {
  red: "#e60000", redDark: "#b30000",
  textMain: "#111", textMuted: "#777",
  whiteSoft: "#fff8ff", subtle: "#e6dfef",
};

// Vista previa de actividad — solo lectura, sin llamadas al backend
export default function ActivityViewerPreview({ form }) {
  const [iframeError, setIframeError] = useState(false);
  const type = form?.type;

  if (!type) return <Typography sx={{ color: COLORS.textMuted }}>Selecciona un tipo de actividad.</Typography>;

  // ── VIDEO ────────────────────────────────────────────────
  if (type === "video") {
    const url = form.video_url?.trim();
    if (!url) return <Alert severity="warning" sx={{ borderRadius: 2 }}>Falta la URL del video.</Alert>;

    // Extraer ID de YouTube
    let videoId = url;
    try {
      const u = new URL(url);
      videoId = u.searchParams.get("v") || url.split("/").pop() || url;
    } catch {}

    return (
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>{form.title}</Typography>
        <Box sx={{ position: "relative", pt: "56.25%", borderRadius: 2, overflow: "hidden", backgroundColor: "#000" }}>
          <iframe
            src={`https://www.youtube.com/embed/${videoId}`}
            title={form.title}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </Box>
      </Box>
    );
  }

  // ── TEXTO ────────────────────────────────────────────────
  if (type === "texto") {
    const url = form.manual_url?.trim();
    const html = form.contenido_html?.trim();
    return (
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>{form.title}</Typography>
        {url && (
          <>
            <Button variant="contained" size="small" endIcon={<FiExternalLink size={13} />}
              href={url} target="_blank" rel="noopener noreferrer"
              sx={{ mb: 1.5, backgroundColor: COLORS.red, "&:hover": { backgroundColor: COLORS.redDark }, borderRadius: 999, textTransform: "none" }}>
              Abrir recurso
            </Button>
            {!iframeError && (
              <Box sx={{ borderRadius: 2, overflow: "hidden", border: `1px solid ${COLORS.subtle}`, height: 400, backgroundColor: COLORS.whiteSoft }}>
                <iframe src={url.includes("drive.google.com") ? url.replace("/view", "/preview") : url}
                  title={form.title} style={{ width: "100%", height: "100%", border: 0 }}
                  onError={() => setIframeError(true)} />
              </Box>
            )}
            {iframeError && <Typography variant="body2" sx={{ color: COLORS.textMuted, mt: 1 }}>No se pudo mostrar la vista previa embebida.</Typography>}
          </>
        )}
        {html && !url && (
          <Box sx={{ p: 2, borderRadius: 2, border: `1px solid ${COLORS.subtle}`, backgroundColor: COLORS.whiteSoft }}
            dangerouslySetInnerHTML={{ __html: html }} />
        )}
        {!url && !html && <Alert severity="warning" sx={{ borderRadius: 2 }}>Falta subir PDF o agregar contenido HTML.</Alert>}
      </Box>
    );
  }

  // ── QUIZ / EXTRAORDINARY ─────────────────────────────────
  if (type === "quiz" || type === "extraordinary") {
    const preguntas = form.preguntas || [];
    return (
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>{form.title}</Typography>
        {preguntas.length === 0
          ? <Alert severity="warning" sx={{ borderRadius: 2 }}>No hay preguntas agregadas aún.</Alert>
          : preguntas.map((p, i) => (
            <Box key={i} sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${COLORS.subtle}`, mb: 1.5, backgroundColor: COLORS.whiteSoft }}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend" sx={{ fontWeight: 600, color: COLORS.textMain, mb: 0.5, fontSize: "0.88rem" }}>
                  {i + 1}. {p.enunciado || `Pregunta ${i + 1}`}
                </FormLabel>
                <RadioGroup>
                  {(p.opciones || []).map((op, j) => (
                    <FormControlLabel key={j} value={op} control={<Radio size="small" disabled />}
                      label={<Typography variant="body2" sx={{ color: p.correcta === j ? "green" : COLORS.textMain, fontWeight: p.correcta === j ? 700 : 400 }}>
                        {op} {p.correcta === j ? "✓" : ""}
                      </Typography>} />
                  ))}
                </RadioGroup>
              </FormControl>
            </Box>
          ))
        }
      </Box>
    );
  }

  // ── ENLACE ───────────────────────────────────────────────
  if (type === "enlace") {
    const url = form.enlace_url?.trim();
    return (
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>{form.title}</Typography>
        {url
          ? <Button variant="outlined" size="small" href={url} target="_blank" rel="noopener noreferrer"
              endIcon={<FiExternalLink size={13} />}
              sx={{ borderColor: COLORS.subtle, color: COLORS.textMain, textTransform: "none" }}>
              Abrir enlace
            </Button>
          : <Alert severity="warning" sx={{ borderRadius: 2 }}>Falta la URL del enlace.</Alert>
        }
      </Box>
    );
  }

  // ── DOCUMENTO / SOLICITUD ────────────────────────────────
  if (type === "documento" || type === "solicitud") {
    return (
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>{form.title}</Typography>
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          {type === "documento"
            ? "El voluntario verá un botón para subir un archivo."
            : "El voluntario verá un formulario de solicitud para aprobar por staff."}
        </Alert>
      </Box>
    );
  }

  // ── SCORM ────────────────────────────────────────────────
  if (type === "scorm") {
    return (
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>{form.title}</Typography>
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          Contenido SCORM. URL: <b>{form.scorm_url || "No configurada"}</b>
        </Alert>
      </Box>
    );
  }

  return (
    <Alert severity="info" sx={{ borderRadius: 2 }}>
      Vista previa no disponible para el tipo <b>{type}</b>.
    </Alert>
  );
}